import React, { useEffect, useState } from "react";
import {
  Card,
  Row,
  Col,
  Typography,
  Progress,
  Tag,
  Button,
  Divider,
  Form,
  Spin,
  Tooltip,
} from "antd";

import {
  Turtle,
  Fish,
  Waves,
  Flame,
  Dumbbell,
  Sailboat,
  Anchor,
  Flag,
  Settings,
  Mountain,
  Medal,
  Armchair,
  FlameKindling,
  Star,
  Shield,
  Trophy,
  Waves as WavesIcon,
  ChevronRight,
  Rocket,
  Droplet,
  Glasses,
  Icon,
  Ship,
  ShipWheel,
  Frame,
  Award,
  BicepsFlexed,
  MessageCircleWarning,
} from "lucide-react";
import DrawerApp from "components/drawer-app";
import ProgressoRadarChart from "./managerial-chart-aqua";
import { shark, bearFace, maskSnorkel } from "@lucide/lab";
import { toRoman } from "views/util/roman";
import { FONT } from "constants/index";
import useBreakpoint from "hooks/useBreakpoint";
import { drawerWidth } from "util/responsive";

const { Title, Text } = Typography;
const ALL_ICONS = {
  "Natação Bebê": <Turtle size={16} />,
  Tartaruguinha: <Turtle size={26} />,
  Peixinho: <Fish size={26} />,
  Golfinho: <Icon iconNode={shark} size={26} />,
  Descobridores: <Droplet size={16} />,
  Desbravador: <Turtle size={26} />,
  Corajoso: <Icon iconNode={bearFace} size={26} />,
  Destemido: <Glasses size={26} />,
  Exploradores: <ShipWheel size={16} />,
  Velejador: <Sailboat size={26} />,
  Marinheiro: <ShipWheel size={26} />,
  Capitão: <Anchor size={26} />,
  Aventureiros: <Ship size={16} />,
  Audacioso: <Settings size={26} />,
  Resiliente: <Dumbbell size={26} />,
  Triunfante: <Medal size={26} />,
  Conquistadores: <Award size={16} />,
  Constante: <BicepsFlexed size={26} />,
  Dedicado: <Frame size={26} />,
  Exemplar: <Star size={26} />,
  Navegadores: <Trophy size={16} />,
  Guerreiro: <Shield size={26} />,
  Campeão: <Trophy size={26} />,
  "Lenda dos Mares": <Icon iconNode={maskSnorkel} size={26} />,
  Iniciante: <Icon iconNode={maskSnorkel} size={16} />,
};

const DescriptionItem = ({ title, style }) => (
  <div
    className="site-description-item-profile-wrapper"
    style={style ? style : { fontFamily: FONT, fontSize: 14, fontWeight: 500 }}
  >
    {title}
  </div>
);

const Tasks = ({ tasks }) => {
  return (
    <div className="aqua-user-task-list custom-scroll">
      {tasks.map((task, index) => {
        const roman = task.order > 0 ? toRoman(task.order) : "I";

        return (
          <Card
            key={index}
            className="aqua-user-task-card"
            style={{ margin: 0 }}
          >
            <Row justify="space-between" align="middle" gutter={[8, 8]}>
              <Col flex="auto" style={{ minWidth: 0 }}>
                <DescriptionItem title={task.name} />
              </Col>
              <Col flex="none">
                <DescriptionItem title={roman} />
              </Col>
            </Row>
          </Card>
        );
      })}
    </div>
  );
};

const AquaPhaseCard = ({ data, onRadarClick, setOpenObjectives }) => {
  const { isMobile } = useBreakpoint();
  const isDisabled = data.disabled;

  return (
    <Card
      className={`aqua-phase-card${isDisabled ? " aqua-phase-card--disabled" : ""}`}
      style={{
        cursor: isDisabled ? "default" : "pointer",
      }}
      onClick={() => {
        if (!isDisabled) {
          setOpenObjectives(data.phases[0]?.objectives ?? [], data.title);
        }
      }}
    >
      <div className="aqua-phase-card-header">
        <div className="aqua-phase-card-title">
          <Title level={5} style={{ marginBottom: 4 }}>
            {data.title}{" "}
            {data.showWarning && (
              <Tooltip title="O aluno já possui as habilidades deste nível e avançou diretamente para a próxima etapa.">
                <MessageCircleWarning color="#fce00a" />
              </Tooltip>
            )}
          </Title>
          <Text className="aqua-phase-card-subtitle">{data.subtitle}</Text>
        </div>
        <Tag
          color={isDisabled ? undefined : "#03DAC6"}
          className="aqua-phase-card-tag"
          style={{
            fontWeight: 500,
            borderRadius: 12,
            padding: isMobile ? "2px 8px" : "4px 12px",
            fontSize: isMobile ? 11 : 12,
            display: "inline-flex",
            alignItems: "center",
            gap: 4,
            margin: 0,
          }}
        >
          {data.icon} {data.completed}/{data.total} fases
        </Tag>
      </div>

      <div className="aqua-subphases-grid">
        {data.phases.map((phase) => {
          const isSubphaseLocked = isDisabled || phase.pct === 0;
          const isSubphaseCompleted = !isDisabled && phase.pct >= 100;

          return (
            <div
              className={`aqua-subphase-card${
                isSubphaseLocked ? " aqua-subphase-card--locked" : ""
              }${isSubphaseCompleted ? " aqua-subphase-card--completed" : ""}`}
              key={phase.name}
            >
              <div className="aqua-subphase-icon">{phase.icon}</div>
              <div className="aqua-subphase-name">{phase.name}</div>
              <div className="aqua-subphase-progress">
                <Progress
                  percent={phase.pct}
                  strokeColor={isSubphaseLocked ? "#c4c4c4" : "#03DAC6"}
                  trailColor={
                    isSubphaseLocked ? "#e8e8e8" : "rgba(255, 255, 255, 0.15)"
                  }
                  showInfo={false}
                  size="small"
                  strokeLinecap="round"
                />
              </div>
            </div>
          );
        })}
      </div>

      <Divider className="aqua-phase-card-divider" />

      <div className="aqua-phase-card-footer">
        <div className="aqua-phase-card-progress">
          <div className="aqua-phase-card-progress-label">
            <span>{isMobile ? "Progresso" : "Progresso total do nível"}</span>
            <span className="aqua-phase-card-progress-value">{data.progress}%</span>
          </div>
          <Progress
            percent={data.progress}
            strokeColor={isDisabled ? "#d9d9d9" : "#03DAC6"}
            trailColor="#f0f0f0"
            showInfo={false}
            size="small"
            strokeLinecap="round"
          />
        </div>
        <Button
          type="default"
          className="aqua-phase-card-radar-btn"
          icon={<ChevronRight size={16} />}
          disabled={isDisabled}
          onClick={(e) => {
            e.stopPropagation();
            onRadarClick(data);
          }}
        >
          Ver Radar
        </Button>
      </div>
    </Card>
  );
};

const Exercises = ({ exercises, tasksDone = [], openTaskExecute }) => {
  return (
    <div className="aqua-user-task-list custom-scroll">
      {exercises.map((exercise, index) => {
        const allTasksExecuted = exercise.tasks.every((task) =>
          tasksDone.some((done) => done.taskId === task.id)
        );

        const executedTasks = exercise.tasks.filter((task) =>
          tasksDone.some((done) => done.taskId === task.id)
        );
        const highest =
          executedTasks.length > 0
            ? executedTasks.reduce((max, task) =>
                task.order > max.order ? task : max
              ).order + 1
            : 1;

        const task = exercise.tasks.find((item) => item.order === highest);
        const roman = highest > 0 ? toRoman(highest) : "I";

        return (
          <Card
            key={index}
            className="aqua-user-task-card"
            style={{ margin: 0 }}
            onClick={() => {
              openTaskExecute(exercise.tasks);
            }}
          >
            <Row justify="space-between" align="middle" gutter={[8, 8]}>
              <Col flex="auto" style={{ minWidth: 0 }}>
                <DescriptionItem title={exercise.name} />
              </Col>
              {!allTasksExecuted && (
                <Col flex="none">
                  <DescriptionItem title={roman} />
                </Col>
              )}
            </Row>
            {allTasksExecuted ? (
              <DescriptionItem
                title={"Todas as tarefas concluídas!"}
                style={{
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: 300,
                  color: "#09ac9c",
                }}
              />
            ) : (
              <DescriptionItem
                title={task?.name}
                style={{
                  fontFamily: FONT,
                  fontSize: 13,
                  fontWeight: 300,
                }}
              />
            )}
          </Card>
        );
      })}
    </div>
  );
};

const DetailAquaUser = ({
  user,
  findSubphaseById,
  calcPercent,
  allPhases = [],
  saveTask,
}) => {
  const { isMobile } = useBreakpoint();
  const [loadingData, setLoadingData] = useState(false);
  const [phaseActual, setPhaseActual] = useState({
    phase: { subphases: [] },
    subphase: {},
  });
  const [form] = Form.useForm();
  const [phases, setPhases] = useState([{ subphases: [] }]);
  const [phasesOptions, setPhasesOptions] = useState([]);
  const [subPhasesOptions, setSubPhasesOptions] = useState([]);
  const [openChart, setOpenChart] = useState(false);
  const [openTask, setOpenTask] = useState(false);
  const [userTasks, setUserTask] = useState([]);
  const [phasesMap, setPhasesMap] = useState([{ phases: [] }]);
  const [phaseId, setPhaseId] = useState(null);
  const [openObjectives, setOpenObjectives] = useState(false);
  const [userSubPhases, setUserSubPhases] = useState([]);
  const [userSubphaseName, setUserSubphaseName] = useState("");

  useEffect(() => {
    setLoadingData(true);
    setPhases(allPhases);
    const filter = user?.isBoot == null ? "ADULT" : "KIDS";
    const phasesOptions = allPhases
      .filter((f) => f.type == filter)
      .map((item) => {
        return { label: item.name, value: item.id };
      });

    setPhasesOptions(phasesOptions);
    const phaseActual = findSubphaseById(user?.training?.subphasesId);
    const subPhasesOptions = phaseActual?.phase?.subphases?.map((item) => {
      return { label: item.name, value: item.id, order: item.order };
    });
    setSubPhasesOptions(subPhasesOptions);
    setPhaseActual(phaseActual);
    const evolution = calcPercent(
      user?.tasksDone ?? [],
      phaseActual?.subphase?.objectives ?? []
    );

    const idSubPhase =
      evolution < 34 ? 1 : evolution > 33 && evolution < 67 ? 2 : 3;
    const subPhaseName = subPhasesOptions?.find(
      (item) => item.order == idSubPhase
    );
    form.setFieldsValue({
      phaseId: phaseActual?.phase?.id,
      subphaseId: subPhaseName?.label,
    });
    const filteredPhases = allPhases.filter((f) => f.type === filter);
    const currentIndex = filteredPhases.findIndex(
      (f) => f.id === phaseActual?.phase?.id
    );

    const phaseMap = filteredPhases.map((f, index) => {
        const isUnlocked = index <= currentIndex;

        const percentPerSubphase = 100 / f.subphases.length;
        let completed =
          index < currentIndex
            ? f.subphases.length
            : index == currentIndex
            ? idSubPhase - 1
            : 0;

        if (f.subphases.length == 1) {
          completed = evolution == 100 ? 1 : 0;
        }
        const evolutionActualPhase = calcPercent(
          user?.tasksDone ?? [],
          f.subphases?.map((item) => item.objectives[0] ?? []) ?? []
        );
        let showWarning = false;
        if (evolutionActualPhase == 0 && isUnlocked) {
          showWarning = true;
        }

        return {
          key: f.id,
          title: f.name,
          subtitle: f.generalObjective,
          icon: ALL_ICONS[f.name],
          completed: completed,
          total: f.subphases.length,
          progress:
            index < currentIndex ? 100 : index == currentIndex ? evolution : 0,
          disabled: !isUnlocked,
          showWarning: showWarning,
          phases: f.subphases
            .sort((a, b) => a.order - b.order)
            .map((sp, indexSB) => {
              const lower = percentPerSubphase * indexSB;
              const upper = percentPerSubphase * (indexSB + 1);
              const progress =
                index < currentIndex
                  ? 100
                  : index == currentIndex
                  ? evolution
                  : 0;
              let pct = 0;

              if (progress >= upper) {
                pct = 100;
              } else if (progress > lower) {
                pct = ((progress - lower) / percentPerSubphase) * 100;
              } else {
                pct = 0;
              }

              if (f.subphases.length == 1) {
                pct = evolution;
              }
              return {
                name: sp.name,
                icon: ALL_ICONS[sp.name],
                pct: Math.round(pct),
                description: "",
                objectives: sp.objectives,
              };
            }),
        };
      });
    setPhasesMap(phaseMap);

    setLoadingData(false);
  }, [user, allPhases]);

  useEffect(() => {
    Object.keys(sessionStorage).forEach((key) => {
      if (key.startsWith("exercise-scroll-aqua-detail")) {
        sessionStorage.removeItem(key);
      }
    });
  }, []);

  const onCloseChart = () => {
    setOpenChart(false);
    setPhaseId(null);
  };

  const closeDrawerObjectives = () => {
    setOpenObjectives(false);
    setUserSubPhases([]);
    setUserSubphaseName("");
  };
  const closeDrawerTask = () => {
    setUserTask([]);
    setOpenTask(false);
  };
  return (
    <Spin spinning={loadingData}>
      <div className="container-aqua-user">
        <Row gutter={[16, 16]} className="aqua-phase-grid">
          {phasesMap.map((phase) => (
            <Col xs={24} lg={12} key={phase.key}>
              <AquaPhaseCard
                data={phase}
                onRadarClick={() => {
                  setOpenChart(true);
                  setPhaseId(phase.key);
                }}
                setOpenObjectives={(objectives, name) => {
                  setOpenObjectives(true);
                  setUserSubPhases(objectives);
                  setUserSubphaseName(name);
                }}
              />
            </Col>
          ))}
        </Row>
      </div>

      <DrawerApp
        open={openChart}
        onClose={onCloseChart}
        title={user?.name}
        body={
          <ProgressoRadarChart
            phases={phases.filter((f) => f.id == phaseId)}
            taskDone={user?.tasksDone ?? []}
          />
        }
        width={drawerWidth(isMobile, 90)}
        isOnlyView={true}
      />
      <DrawerApp
        open={openObjectives}
        onClose={closeDrawerObjectives}
        title={"Objetivos - " + userSubphaseName}
        body={
          <Exercises
            exercises={userSubPhases}
            tasksDone={user?.tasksDone}
            openTaskExecute={(tasks) => {
              setUserTask(tasks);
              setOpenTask(true);
            }}
          />
        }
        width={drawerWidth(isMobile, 40)}
        isOnlyView={true}
      />
      <DrawerApp
        open={openTask}
        onClose={closeDrawerTask}
        title={"Atividades"}
        body={<Tasks tasks={userTasks} />}
        width={drawerWidth(isMobile, 40)}
        isOnlyView={true}
      />
    </Spin>
  );
};

export default DetailAquaUser;
